// src/services/schoolConfigService.ts
// Service for managing school configuration data

import { supabase, DB_SCHEMA } from '../supabase/config';
import {
  SchoolConfiguration,
  SchoolConfigurationRow,
  CurriculumArea,
  CurriculumAreaRow,
  LevelCode,
  LevelCodeRow,
  ConfigurableGridType,
  GridTypeRow,
  rowToSchoolConfig,
  schoolConfigToRow,
  rowToCurriculumArea,
  curriculumAreaToRow,
  rowToLevelCode,
  levelCodeToRow,
  rowToGridType,
  DEFAULT_SCHOOL_CONFIG,
} from '../types/schoolConfig';

// ============================================================================
// School Configuration
// ============================================================================

// Multi-tenant note: school_configuration holds ONE row per tenant, and RLS
// (tenant_isolation) ensures a caller only ever sees their own tenant's row.
// We therefore read/write the single RLS-visible row rather than a hardcoded
// tenant id. This works both before fail-closed (one row total = tenant #1) and
// after (each tenant sees only its own row). See docs/planning/proposed-migrations/
// 0003_per_tenant_school_configuration.sql.

/**
 * Fetch the school configuration for the current tenant.
 */
export async function fetchSchoolConfig(): Promise<SchoolConfiguration> {
  const { data, error } = await supabase
    .from('school_configuration')
    .select('*')
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching school configuration:', error);
    // Return default config if database is unavailable
    return DEFAULT_SCHOOL_CONFIG;
  }

  if (!data) return DEFAULT_SCHOOL_CONFIG;

  return rowToSchoolConfig(data as SchoolConfigurationRow);
}

/**
 * Update the current tenant's school configuration.
 */
export async function updateSchoolConfig(
  updates: Partial<SchoolConfiguration>
): Promise<SchoolConfiguration> {
  const row = schoolConfigToRow(updates);

  // Target the caller's own configuration row (resolved via RLS), not a
  // hardcoded id — otherwise every tenant would write tenant #1's config.
  const { data: existing, error: lookupError } = await supabase
    .from('school_configuration')
    .select('id')
    .limit(1)
    .maybeSingle();

  if (lookupError || !existing) {
    const reason = lookupError?.message ?? 'no configuration row visible to caller';
    console.error('Error locating school configuration to update:', reason);
    throw new Error(`Failed to update school configuration: ${reason}`);
  }

  const { data, error } = await supabase
    .from('school_configuration')
    .update(row)
    .eq('id', (existing as { id: string }).id)
    .select()
    .single();

  if (error) {
    console.error('Error updating school configuration:', error);
    throw new Error(`Failed to update school configuration: ${error.message}`);
  }

  return rowToSchoolConfig(data as SchoolConfigurationRow);
}

/**
 * Subscribe to school configuration changes
 */
export function subscribeToSchoolConfig(
  callback: (config: SchoolConfiguration) => void
): () => void {
  const channel = supabase
    .channel('school_configuration_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: DB_SCHEMA,
        table: 'school_configuration',
      },
      (payload) => {
        if (payload.new) {
          callback(rowToSchoolConfig(payload.new as SchoolConfigurationRow));
        }
      }
    )
    .subscribe();

  // Return unsubscribe function
  return () => {
    supabase.removeChannel(channel);
  };
}

// ============================================================================
// Curriculum Areas
// ============================================================================

/**
 * Fetch all curriculum areas
 */
export async function fetchCurriculumAreas(): Promise<CurriculumArea[]> {
  const { data, error } = await supabase
    .from('curriculum_areas')
    .select('*')
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching curriculum areas:', error);
    return [];
  }

  return (data as CurriculumAreaRow[]).map(rowToCurriculumArea);
}

/**
 * Fetch active curriculum areas only
 */
export async function fetchActiveCurriculumAreas(): Promise<CurriculumArea[]> {
  const { data, error } = await supabase
    .from('curriculum_areas')
    .select('*')
    .eq('is_active', true)
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching active curriculum areas:', error);
    return [];
  }

  return (data as CurriculumAreaRow[]).map(rowToCurriculumArea);
}

/**
 * Create a new curriculum area
 */
export async function createCurriculumArea(
  area: Omit<CurriculumArea, 'id' | 'createdAt' | 'updatedAt'>
): Promise<CurriculumArea> {
  const row = curriculumAreaToRow(area);

  const { data, error } = await supabase
    .from('curriculum_areas')
    .insert(row)
    .select()
    .single();

  if (error) {
    console.error('Error creating curriculum area:', error);
    throw new Error(`Failed to create curriculum area: ${error.message}`);
  }

  return rowToCurriculumArea(data as CurriculumAreaRow);
}

/**
 * Update a curriculum area
 */
export async function updateCurriculumArea(
  id: string,
  updates: Partial<CurriculumArea>
): Promise<CurriculumArea> {
  const row = curriculumAreaToRow(updates);

  const { data, error } = await supabase
    .from('curriculum_areas')
    .update(row)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating curriculum area:', error);
    throw new Error(`Failed to update curriculum area: ${error.message}`);
  }

  return rowToCurriculumArea(data as CurriculumAreaRow);
}

/**
 * Delete a curriculum area
 */
export async function deleteCurriculumArea(id: string): Promise<void> {
  const { error } = await supabase
    .from('curriculum_areas')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting curriculum area:', error);
    throw new Error(`Failed to delete curriculum area: ${error.message}`);
  }
}

/**
 * Subscribe to curriculum areas changes
 */
export function subscribeToCurriculumAreas(
  callback: (areas: CurriculumArea[]) => void
): () => void {
  const channel = supabase
    .channel('curriculum_areas_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: DB_SCHEMA,
        table: 'curriculum_areas',
      },
      async () => {
        // Refetch all areas on any change
        const areas = await fetchCurriculumAreas();
        callback(areas);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ============================================================================
// Level Codes
// ============================================================================

/**
 * Fetch all level codes
 */
export async function fetchLevelCodes(): Promise<LevelCode[]> {
  const { data, error } = await supabase
    .from('level_codes')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('Error fetching level codes:', error);
    return [];
  }

  return (data as LevelCodeRow[]).map(rowToLevelCode);
}

/**
 * Fetch active level codes only
 */
export async function fetchActiveLevelCodes(): Promise<LevelCode[]> {
  const { data, error } = await supabase
    .from('level_codes')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('Error fetching active level codes:', error);
    return [];
  }

  return (data as LevelCodeRow[]).map(rowToLevelCode);
}

/**
 * Create a new level code
 */
export async function createLevelCode(
  level: Omit<LevelCode, 'id' | 'createdAt' | 'updatedAt'>
): Promise<LevelCode> {
  const row = levelCodeToRow(level);

  const { data, error } = await supabase
    .from('level_codes')
    .insert(row)
    .select()
    .single();

  if (error) {
    console.error('Error creating level code:', error);
    throw new Error(`Failed to create level code: ${error.message}`);
  }

  return rowToLevelCode(data as LevelCodeRow);
}

/**
 * Update a level code
 */
export async function updateLevelCode(
  id: string,
  updates: Partial<LevelCode>
): Promise<LevelCode> {
  const row = levelCodeToRow(updates);

  const { data, error } = await supabase
    .from('level_codes')
    .update(row)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating level code:', error);
    throw new Error(`Failed to update level code: ${error.message}`);
  }

  return rowToLevelCode(data as LevelCodeRow);
}

/**
 * Delete a level code
 */
export async function deleteLevelCode(id: string): Promise<void> {
  const { error } = await supabase
    .from('level_codes')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting level code:', error);
    throw new Error(`Failed to delete level code: ${error.message}`);
  }
}

/**
 * Subscribe to level codes changes
 */
export function subscribeToLevelCodes(
  callback: (levels: LevelCode[]) => void
): () => void {
  const channel = supabase
    .channel('level_codes_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: DB_SCHEMA,
        table: 'level_codes',
      },
      async () => {
        const levels = await fetchLevelCodes();
        callback(levels);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ============================================================================
// Grid Types
// ============================================================================

/**
 * Fetch all grid types
 */
export async function fetchGridTypes(): Promise<ConfigurableGridType[]> {
  const { data, error } = await supabase
    .from('grid_types')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching grid types:', error);
    return [];
  }

  return (data as GridTypeRow[]).map(rowToGridType);
}

/**
 * Fetch a single grid type by ID
 */
export async function fetchGridTypeById(id: string): Promise<ConfigurableGridType | null> {
  const { data, error } = await supabase
    .from('grid_types')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching grid type:', error);
    return null;
  }

  return rowToGridType(data as GridTypeRow);
}

/**
 * Subscribe to grid types changes
 */
export function subscribeToGridTypes(
  callback: (types: ConfigurableGridType[]) => void
): () => void {
  const channel = supabase
    .channel('grid_types_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: DB_SCHEMA,
        table: 'grid_types',
      },
      async () => {
        const types = await fetchGridTypes();
        callback(types);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ============================================================================
// Utility functions
// ============================================================================

/**
 * Get curriculum area by code
 */
export function getCurriculumAreaByCode(
  areas: CurriculumArea[],
  code: string
): CurriculumArea | undefined {
  return areas.find((area) => area.code === code);
}

/**
 * Get level code by code
 */
export function getLevelCodeByCode(
  levels: LevelCode[],
  code: string
): LevelCode | undefined {
  return levels.find((level) => level.code === code);
}

/**
 * Build lookup maps for performance
 */
export function buildCurriculumAreaMap(
  areas: CurriculumArea[]
): Map<string, CurriculumArea> {
  return new Map(areas.map((area) => [area.code, area]));
}

export function buildLevelCodeMap(
  levels: LevelCode[]
): Map<string, LevelCode> {
  return new Map(levels.map((level) => [level.code, level]));
}
